import { useTranslation } from './src/i18n';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { C } from './src/constants/theme';
import { GRADIENT_SIDEBAR } from '@parsina/framework/tokens';
import { logout, getToken, parseJwt } from './src/lib/auth.js';
import { useAppNavigate, stripClinic } from './src/hooks/useAppNavigate.js';
import { accountUrl } from './src/lib/runtimeEnv.js';
import { useClinicCapabilities } from './src/hooks/useSubscription.js';
import { useCredits } from './src/hooks/useCredits.js';
import { useMyProfile } from './src/hooks/useMyProfile.js';
import SubscriptionBanner from './src/components/common/SubscriptionBanner.jsx';
import PaywallDialog from './src/components/common/PaywallDialog.jsx';
import InstallPrompt from './src/components/common/InstallPrompt.jsx';
import ClinicSwitcher from './src/components/common/ClinicSwitcher.jsx';
import { useClinicContext } from './src/context/ClinicContext.jsx';
import { useAccess } from './src/hooks/useAccess.js';
import { visibleNavIds, PERSON_NAV } from './src/constants/nav.js';
import { NAV_ITEMS } from './src/constants/navItems.jsx';
import { Alert } from '@parsina/framework';
import SidebarFooter from './src/components/common/SidebarFooter.jsx';
import useResponsive from './src/hooks/useResponsive.js';
import MyAccessDrawer from './src/components/common/MyAccessDrawer.jsx';
import SecretaryHost, { SecretaryProvider } from './src/secretary/SecretaryHost.jsx';
import SecretaryFab from './src/secretary/SecretaryFab.jsx';
import SecretaryRecorder from './src/secretary/SecretaryRecorder.jsx';
import MobileBottomNav from './src/mobile/MobileBottomNav.jsx';
import MobileMoreSheet from './src/mobile/MobileMoreSheet.jsx';
import { bottomNavPlan } from './src/mobile/bottomNav.js';

// Glyphs for the footer menu. App-local, like NAV_ITEMS below: the footer owns
// the menu's shape, not its destinations, so it owns none of their icons either.
const ICON = {
  account: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" /></svg>,
  coin: <svg viewBox="0 0 24 24" fill="none" width="17" height="17"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" /><path d="M12 7.5v9M9.5 10h5M9.5 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
  access: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>,
  // Not mirrored: the arrow points out of a door, not along the reading direction.
  logout: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" /></svg>,
};

// Exported so a test can assert every item has a route — an id that useAppNavigate's
// ROUTES map doesn't know makes the sidebar button a silent no-op.
/* NAV_ITEMS moved to `src/constants/navItems.jsx` so the phone's bottom bar can
   read the same labels and glyphs without importing the desktop layout that
   renders it (which would be a cycle). Re-exported here because the layout was
   its published home and tests import it from this path. */
export { NAV_ITEMS };

const MINI = 68;
const FULL = 260;

const ClinicLayout = ({ children }) => {
  const { t, formatNumber } = useTranslation();
  // The SIGNED-IN PERSON's own پارسه — every member of staff has one, and pays
  // from it for what they do (there is no clinic pool). `balance` is what a new
  // charge can take. Null only when the wallet could not be read, and then the
  // card does not render, because there is nothing true to put in it.
  const { balance: myBalance, level: personalLevel } = useCredits();
  const personalCredit = myBalance === null ? null : { balance: myBalance };
  // Held at the shell so the banner is consistent on every page and the
  // capabilities query runs once per session rather than once per screen.
  const { capabilities } = useClinicCapabilities();
  const [preferCollapsed, setPreferCollapsed] = React.useState(false);
  // The visit room's own rail state, `{ path, collapsed }` — see `collapsed`.
  const [visitRail, setVisitRail] = React.useState(null);
  const navigate = useAppNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  // Owned by the shell rather than by the bar, because both the bar's «بیشتر»
  // fold and (on the desktop) the sidebar's switcher open the same surfaces.
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [accessOpen, setAccessOpen] = React.useState(false);

  // Sidebar footer identity from the Keycloak token (the signed-in user),
  // not a hardcoded "کارمند کلینیک" role label.
  const kc = React.useMemo(() => parseJwt(getToken()) || {}, []);
  const kcName = kc.name || [kc.given_name, kc.family_name].filter(Boolean).join(' ') || kc.preferred_username || t('common.roles.user');
  // Staff who signed up by phone and never filled in a name have no `name` and
  // no `email`, so BOTH lines fell through to `preferred_username` and the row
  // printed the same phone number twice — which reads as a rendering fault, not
  // as an account. The second line only earns its place when it says something
  // the first does not — and when it has nothing to say it is EMPTY, not the
  // product's name («پنل مدیریت»), which described the screen the person was
  // already looking at. The footer centres a one-line identity on its own.
  const kcSubtitleRaw = kc.email || kc.preferred_username || '';
  const kcSubtitle = kcSubtitleRaw && kcSubtitleRaw !== kcName ? kcSubtitleRaw : '';

  // The person's own picture, which the panel had never asked for: the kit's
  // Avatar has always accepted `src`, the account app writes Keycloak's
  // `picture` attribute, and `myProfile.avatar` reads it back — but the footer
  // was handed a name and nothing else, so it drew initials for everyone. Two
  // initial discs on one rail is most of why the person's mark and the clinic's
  // read as the same kind of object.
  //
  // NAME and SUBTITLE deliberately stay on the token: they are there before the
  // first paint, and swapping them for a fetched value would make the identity
  // row flicker on every load. Only the picture waits, and an Avatar with no
  // `src` yet is already showing the correct fallback.
  //
  // Settings runs its own `useMyProfile`, so changing the picture there does not
  // repaint this one until the next load. Worth a shared profile context the day
  // a third caller appears; not worth one for two.
  const { profile } = useMyProfile();
  const kcAvatar = profile?.avatar || undefined;

  const access = useAccess();
  const {
    pendingInvitations, activeMembership, accessChanged, dismissAccessChange, reload,
    // The phone has no sidebar to hang the switcher off, so the shell reads the
    // membership list itself and hands it to the more-sheet (§3.5). The FULL
    // list, not just the enterable one: a blocked clinic is shown, dimmed, with
    // its reason — hiding it answers "where is my other clinic?" with silence.
    memberships, activeRef, canSwitch, switchClinic,
  } = useClinicContext();

  // Matched against the path WITHOUT its `/c/:ref` prefix. Every one of these
  // comparisons was written against a bare path, and the tenant segment would have
  // made all of them false at once — silently un-highlighting every nav row and
  // making the subscription banner appear on «کلینیک من», which it is suppressed on.
  const barePath = stripClinic(location.pathname);

  // The visit room OPENS with the rail collapsed — it needs every column it can
  // get. A starting state, not the person's choice: they may expand it for the
  // visit, leaving puts back whatever they had, and the next visit opens
  // collapsed again. Derived rather than set by an effect, so a visit never
  // paints expanded and then snaps shut; keyed by path, so the next patient
  // starts collapsed. (The doctor panel's WebLayout does the same.)
  const onVisit = barePath.startsWith('/visit/');
  const collapsed = onVisit
    ? (visitRail?.path === location.pathname ? visitRail.collapsed : true)
    : preferCollapsed;
  React.useEffect(() => { if (!onVisit) setVisitRail(null); }, [onVisit]);
  const toggleCollapsed = () => {
    if (onVisit) setVisitRail({ path: location.pathname, collapsed: !collapsed });
    else setPreferCollapsed((c) => !c);
  };
  const onClinicPage = barePath.startsWith('/clinic');

  const currentScreen = NAV_ITEMS(t).find(item =>
    barePath === '/' + item.id ||
    barePath.startsWith('/' + item.id + '/')
  )?.id || 'reception';

  // FILTERED, never disabled. An entry that leads to a denial is worse than no
  // entry: a warehouse keeper who can see «صندوق» greyed out learns only that the
  // product is withholding something from them (design §5.8).
  //
  // Derived from the SAME `myAccess` payload the BFF enforces on, so the sidebar
  // and the boundary cannot disagree — the client is not making an independent
  // judgement about permissions, it is rendering the one the server made.
  const allowed = React.useMemo(() => new Set(visibleNavIds(access)), [access]);
  const navItems = React.useMemo(() => NAV_ITEMS(t).filter((item) => {
    const person = PERSON_NAV.find((n) => n.id === item.id);
    if (person) return person.when({ pendingInvitations });
    return allowed.has(item.id);
  }), [t, allowed, pendingInvitations]);

  // The browser tab is the only thing distinguishing two tabs on two clinics in
  // the tab strip, and this design makes that a routine arrangement (§A15).
  React.useEffect(() => {
    const screen = NAV_ITEMS(t).find((i) => i.id === currentScreen)?.label;
    const parts = [screen, activeMembership?.clinicName, t('common.brand.name')].filter(Boolean);
    document.title = parts.join(' · ');
  }, [t, currentScreen, activeMembership]);

  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  /* Alert zone ② and the routed screen — identical on both surfaces, because
     they are content, not chrome. Only the navigation is forked. */
  const alertZone = (
    <>
      {onClinicPage ? null : (
        <SubscriptionBanner
          capabilities={capabilities}
          onRenew={() => navigate('subscription')}
          /* Dismissible on a phone only. On a wide monitor the banner costs a
             strip; on a phone it costs a slice of the one screen there is,
             above the list being worked. The dismissal is remembered against
             the STATUS, so the trial nudge goes for good while a later lapse
             still speaks — see the note in SubscriptionBanner. */
          dismissible={isMobile}
          clinicRef={activeRef}
        />
      )}
      {accessChanged ? (
        <div style={{ padding: '10px 16px 0' }}>
          <Alert
            variant="info"
            action={(
              <button
                type="button"
                onClick={async () => { dismissAccessChange(); await reload({ silent: true }); }}
                style={{
                  background: 'transparent', border: 'none', color: C.navy,
                  fontWeight: 800, fontSize: 12.5, cursor: 'pointer',
                  fontFamily: 'Meshkat, sans-serif', padding: 4,
                }}
              >
                {t('clinic.context.accessChangedAction')}
              </button>
            )}
          >
            {t('clinic.context.accessChanged')}
          </Alert>
        </div>
      ) : null}
    </>
  );

  /* ── The phone (docs/MOBILE-REDESIGN.md §3) ──────────────────────────────
     Bottom navigation instead of a sidebar, and no top bar at all: each screen
     brings its own gradient header. The 260px rail is not narrowed here, it is
     not rendered — a fixed rail beside the content is what pushed every screen
     into horizontal scroll on a phone.

     The screen owns the viewport: this wrapper does not scroll, and each
     `X.mobile.jsx` renders inside `MobileScreen`, which holds the page's one
     scroll region. */
  // Both shells sit in ONE SecretaryProvider at the same place in the tree, so a
  // recording, or a result still waiting, survives the window crossing the width.
  if (isMobile) {
    return (
      <SecretaryProvider>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: C.bg }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {alertZone}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</div>
        </div>

        <MobileBottomNav
          access={access}
          barePath={barePath}
          pendingCount={pendingInvitations.length}
          onNavigate={(id) => navigate(id)}
          onOpenMore={() => setMoreOpen(true)}
        />

        <MobileMoreSheet
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          overflow={bottomNavPlan(access).overflow}
          /* `opts` travels: the sheet asks for `replace` because it is handing
             the destination its own history entry. Dropping the argument here
             is what made every row in the fold a no-op — the push landed and
             the sheet's deferred release popped it straight back off. */
          onNavigate={(id, opts) => navigate(id, null, opts)}
          user={{ name: kcName, subtitle: kcSubtitle, avatarUrl: kcAvatar }}
          personalCredit={personalCredit}
          personalLevel={personalLevel}
          accountHref={accountUrl('/')}
          creditsHref={accountUrl('/credits')}
          memberships={memberships}
          activeRef={activeRef}
          canSwitch={canSwitch}
          onSwitchClinic={(m) => switchClinic(m.slug || m.clinicId)}
          pendingInvitations={pendingInvitations}
          onOpenAccess={() => setAccessOpen(true)}
          onSignOut={() => logout()}
        />

        <MyAccessDrawer open={accessOpen} onClose={() => setAccessOpen(false)} />

        <PaywallDialog onRenew={() => navigate('subscription')} />
        <InstallPrompt isMobile />
        {/* «منشی هوشمند» on a phone: the record button in the thumb's corner instead
            of a sidebar row and F1 (SecretaryFab.jsx); its results drop from the top. */}
        <SecretaryHost />
        <SecretaryFab hidden={moreOpen || accessOpen} />
      </div>
      </SecretaryProvider>
    );
  }

  return (
    <SecretaryProvider>
    <div style={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden' }}>

      {/* Sidebar */}
      <div data-testid="app-sidebar" data-collapsed={collapsed ? 'true' : 'false'} style={{
        width: collapsed ? MINI : FULL,
        flexShrink: 0,
        background: GRADIENT_SIDEBAR,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
        zIndex: 10,
      }}>

        {/* Header — the clinic context switcher.
            It replaces the static «پارسینا / پنل کلینیک» brand block, which
            answered a question nobody asks twice, and it takes the 68px slot the
            collapse toggle used to own. Both cannot survive the collapsed rail,
            and switching context is a many-times-a-day action while collapsing is
            a once-a-session one — so the toggle moved to the footer (§A6). */}
        <div style={{
          height: 68,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <ClinicSwitcher collapsed={collapsed} />
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, paddingTop: 10, overflowY: 'auto', overflowX: 'hidden' }}>
          {navItems.map(item => {
            const active = currentScreen === item.id;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.id)}
                title={collapsed ? item.label : undefined}
                style={{
                  // Rounded pill inset 10px from the sidebar edges. The icon cell is
                  // 48px, so the icon's optical center stays at 34px from the right
                  // edge — identical in collapsed (68px) and expanded (260px) states.
                  width: 'calc(100% - 20px)', margin: '2px 10px',
                  display: 'flex', alignItems: 'center',
                  padding: 0, border: 'none', borderRadius: 10,
                  background: active ? C.navy : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(0,0,0,0.22)' : 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.18s, box-shadow 0.18s',
                  fontFamily: 'Meshkat, sans-serif',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width: MINI - 20, // pill is inset 10px each side of the MINI column
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '11px 0',
                  color: active ? 'white' : 'rgba(255,255,255,0.55)',
                }}>
                  {item.icon}
                </span>
                <span style={{
                  ...fade,
                  flex: 1,
                  // A <button> centres its content by default, so without this the
                  // label floats in the middle of the row. `start` follows the
                  // document direction: right edge in Persian/Arabic, left in English.
                  textAlign: 'start',
                  color: active ? 'white' : 'rgba(255,255,255,0.55)',
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                }}>
                  {item.label}
                </span>
                {/* The count is the whole reason the row is there. On the collapsed
                    rail the label has faded out, so the badge moves onto the icon
                    as a dot — an invitation nobody can see is an invitation nobody
                    accepts. */}
                {item.id === 'invitations' && pendingInvitations.length ? (
                  <span
                    aria-hidden={false}
                    aria-label={t('nav.items.invitationsCount', { count: pendingInvitations.length })}
                    style={{
                      position: collapsed ? 'absolute' : 'static',
                      insetInlineEnd: collapsed ? 14 : undefined,
                      insetBlockStart: collapsed ? 8 : undefined,
                      minWidth: 18, height: 18, borderRadius: 999,
                      background: C.teal, color: 'white',
                      fontSize: 10.5, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 5px', marginInlineEnd: collapsed ? 0 : 12, flexShrink: 0,
                    }}
                  >
                    {formatNumber(pendingInvitations.length)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {/* ── Footer ──
            Two grouped pieces (see SidebarFooter for the reasoning): a utility
            strip — the collapse toggle, rehoused from the header the clinic
            switcher owns (§A6), and the way into the `?` overlay — and beneath
            it ONE card for the signed-in person: who they are, and their own
            پارسه as a second row of the same card.

            THIS IS THE PERSON'S BALANCE — the only پارسه there is. The clinic
            has no pool: whoever dictates or sends pays from their own wallet, and
            ownership is said by where it sits, under the person's name. `credit`
            is null only when the wallet could not be read, and the row is then
            not rendered at all: there is no number here to be wrong about.

            Name, family name, password and language are not this panel's to
            write — one account backs every Parsina panel, so an editor here
            would be a second writer against the same person. «حساب پارسینا» is a
            plain <a> on the same realm and Keycloak client, so the account app
            runs its own PKCE flow and the SSO session returns a token with no
            second sign-in; `accountUrl` carries return_url so «بازگشت» comes
            back here.

            Topping up a PERSONAL balance also belongs to the account app, not to
            this panel — and there is no `/credits` route here to send anyone to
            (`useAppNavigate` maps the id, `App.jsx` defines no route, so it
            would land silently on پذیرش).

            «دسترسی‌های من» sits in this menu as well as in the clinic switcher's:
            «what may I do here?» is a question about the person as much as about
            the clinic, and the person's menu is where someone looks first.

            The AR/EN/FA chips that used to live here are still gone. The switch
            argued for its own place in the sidebar — «the person who needs it is
            the person who cannot read the panel» — and that argument still
            holds, but the destination is the account row: one press away, with
            an icon that survives the collapsed rail, and the account app renders
            in the person's own language when they arrive. Keeping chips as well
            would put a second writer against the same Keycloak `locale`. */}
        {/* «منشی هوشمند» — the record button, at the foot of the rail with its results
            stacked beside it (SecretaryRecorder.jsx). It is a part of the shell, not a
            destination, so it has no route and is not a NAV_ITEMS row. */}
        <SecretaryRecorder collapsed={collapsed} iconCell={MINI - 22} />

        <SidebarFooter
          data-testid="account-card"
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          menuLabel={t('nav.account.menuLabel')}
          user={{ name: kcName, subtitle: kcSubtitle, avatarUrl: kcAvatar }}
          credit={!personalCredit ? null : {
            // Already localised by the app — the footer renders digits verbatim.
            amount: formatNumber(personalCredit.balance),
            level: personalLevel,
            unit: t('nav.credits.unit'),
            // A real <a>, as this row has always been: middle-click and
            // «open in new tab» must keep working on a link out of the panel.
            href: accountUrl('/credits'),
          }}
          items={[
            {
              key: 'account',
              label: t('nav.actions.account'),
              description: t('nav.account.parsinaHint'),
              icon: ICON.account,
              href: accountUrl('/'),
              external: true,
              'data-testid': 'sidebar-account',
            },
            personalCredit && {
              key: 'credits',
              label: t('nav.credits.topUp'),
              icon: ICON.coin,
              href: accountUrl('/credits'),
              external: true,
            },
            {
              key: 'access',
              label: t('clinic.myAccess.title'),
              icon: ICON.access,
              onClick: () => setAccessOpen(true),
              'data-testid': 'sidebar-access',
            },
            {
              key: 'logout',
              label: t('nav.actions.signOut'),
              icon: ICON.logout,
              tone: 'destructive',
              separatorBefore: true,
              onClick: () => logout(),
              'data-testid': 'sidebar-signout',
            },
          ].filter(Boolean)}
        />

      </div>

      {/* Page content.

          The ground is painted HERE, not left to whatever is behind. `html, body`
          are `#0f1923` — the dark the sidebar gradient sits on and the colour an
          overscroll should reveal — so any screen shorter than the viewport used
          to end in a navy band across the page. A skeleton is the common case,
          because a placeholder is often shorter than the data that replaces it,
          but a short real page did it too. */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', background: C.bg,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Canonical alert zone ② — the subscription banner and the
            access-changed notice, shared verbatim with the mobile shell above
            so a lapse or a role change cannot be announced on one surface and
            not the other. Their reasoning is recorded where `alertZone` is
            built. */}
        {alertZone}
        {/* The screen gets what is LEFT under the alert zone, not the whole
            column. A screen that scrolls inside itself (`height: 100%` + its own
            scroller — بیماران, حسابداری …) used to take the full column height
            and end one banner's height below the fold, which hid whatever it
            pins to its bottom (the patients selection bar, PM-0053). A screen
            taller than the space still overflows this box, and the column
            scrolls it exactly as before. */}
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      </div>

      {/*
        Mounted once, for the whole app. A `SUBSCRIPTION_REQUIRED` failure can
        come from any screen — booking, adding a doctor, defining a role — and
        every one of them needs the same explanation and the same way out.
      */}
      <PaywallDialog onRenew={() => navigate('subscription')} />
      <MyAccessDrawer open={accessOpen} onClose={() => setAccessOpen(false)} />
      <SecretaryHost sidebarWidth={collapsed ? MINI : FULL} />
      {/* «نصب روی این رایانه» — the Windows desk install (src/lib/pwa.js). */}
      <InstallPrompt />
    </div>
    </SecretaryProvider>
  );
};

export default ClinicLayout;
